import { useMemo, useState, useRef, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import { chain, filter, includes } from "lodash"
import { IconButton, Menu } from "@mui/material"
import { MoreVert } from "@mui/icons-material"
import { useQueryStringState } from "hooks/query-string"
import { getQueryStringState } from "helpers/queryString"
import { useSaveContextPreferences } from "hooks/preferences"
import { LONG, LONG_SHORT } from "resources/position-sets/composition"
import CompareSearch from "modules/comparison/components/compare-search"
import { Creators as DialogCreators } from "modules/dialog/model"
import { useAccountAlias } from "resources/account"
import { useModelMetadata } from "resources/models"
import OPTooltip from "components/material/tooltip"
import AnalyzeHeaderFilters from "components/analyze-header-filters"
import { computeNonNullSegment, filterRowsSupportContribution, filterSupportsContribution, removeUnsupportedBaseFilters } from "helpers/segments"
import { OK, LOADING, ERROR } from "helpers/data-statuses"
import { DATE } from "helpers/shapes"
import { BASKET, BENCHMARK, CONTRIBUTION, ETF, INDEX, NOT_FOUND_LABEL, PORTFOLIO, POSITION_SET, STANDALONE } from "../position-sets"
import useSegmentFilterOptions from "../hooks/use-segment-filter-options"
import { computeSegmentFilterUpdate } from "../helpers/segment-filters"
import ItemHeaderMenuItem from "./item-header-menu-item"

const EMPTY_ARRAY = []

// Used by handleApplySegmentNormalization - see filterSupportsContribution.
function computeFallbackNormalization(targetNormalization, supportsContribution) {
  return targetNormalization === CONTRIBUTION && !supportsContribution ? STANDALONE : targetNormalization
}

function generateBaseSelectionActions(addBase) {
  return {
    benchmark: {
      mainAction: item => () => { addBase({ type: BENCHMARK, id: item.id }) },
      noDispatch: true,
    },
    index: {
      mainAction: item => () => { addBase({ type: INDEX, id: item.id }) },
      noDispatch: true,
    },
    security: {
      mainAction: item => () => {
        let newPositionSet
        switch (true) {
          case item.assetClass === "swap":
            newPositionSet = { type: BASKET, id: item.ticker }
            break
          case item.assetSubclass === "etf":
            newPositionSet = { type: ETF, id: item.internalId }
            break
        }

        addBase(newPositionSet)
      },
      noDispatch: true,
    },
    portfolio: {
      mainAction: item => () => { addBase({ type: PORTFOLIO, id: item.alias }) },
      noDispatch: true,
    },
  }
}

export default function CompareItemHeader({ modelId, positionSet, index, remainingColumns, isContextActive, endDate, pagePortfolio }) {
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()
  const [modelMetadata] = useModelMetadata(modelId)
  const [anchorEl, setAnchorEl] = useState(null)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  // Ref, not state: must hold this tick's value when setNormalization reads it, not next render's.
  const pendingFilterRowsRef = useRef(null)
  const [{ positionSets: queryStringPositionSets = EMPTY_ARRAY }, updateQueryString] = useQueryStringState()
  const savePositionSetsPreference = useSaveContextPreferences("positionSets")

  const modelSupportsComposites = useMemo(
    () => !includes(modelMetadata?.missingDataFiles, "composite"),
    [modelMetadata],
  )

  const disableSearchResult = useCallback((asset) => {
    if (asset.assetSubclass === "stock") {
      return true
    }
    if (!modelSupportsComposites && asset.assetSubclass === "etf") {
      return true
    }
    return false
  }, [modelSupportsComposites])

  const currentIndex = index - 1

  const instrument = useMemo(() => ({
    type: positionSet.queryStringPositionSet?.type,
    id: positionSet.queryStringPositionSet?.id,
  }), [positionSet.queryStringPositionSet])

  const currentSegmentFilters = positionSet.queryStringPositionSet?.segment?.filters || null
  const currentNormalization = positionSet.normalization || CONTRIBUTION
  const isColumnActive = positionSet.isActive
  const { isExperimentOfOwnPortfolio, keepsBaseSegment: canKeepSegment } = positionSet
  const filterOnsNormalization = canKeepSegment || !isColumnActive ? currentNormalization : CONTRIBUTION

  const { filterOns, addedFilters, securityIds, status: filterOptionsStatus } = useSegmentFilterOptions(
    accountAlias,
    modelId,
    instrument,
    endDate,
    currentSegmentFilters,
    filterOnsNormalization,
    isColumnActive,
    isExperimentOfOwnPortfolio,
    positionSet.baseName ?? null,
  )

  const hasUnsupportedContributionFilter = !filterSupportsContribution(currentSegmentFilters, isColumnActive)

  const isSegmentFilterPickerDisabled = filterOns.length === 0 || filterOptionsStatus !== OK
  const segmentFilterPickerDisabledTooltip = filterOptionsStatus === LOADING
    ? "Filters are still loading for this position"
    : filterOptionsStatus === ERROR
      ? "Filters failed to load for this position"
      : "No filters are available for this position"

  const applyPositionSetUpdate = useCallback((updatedPositionSet) => {
    const currentPositionSets = getQueryStringState().positionSets ?? EMPTY_ARRAY
    const newPositionSets = [
      ...currentPositionSets.slice(0, currentIndex),
      updatedPositionSet,
      ...currentPositionSets.slice(currentIndex + 1),
    ]
    updateQueryString({ positionSets: newPositionSets })
    savePositionSetsPreference(newPositionSets)
  }, [currentIndex, updateQueryString, savePositionSetsPreference])

  const updateCurrentPositionSet = useCallback((updatedPositionSet) => {
    setAnchorEl(false)
    applyPositionSetUpdate(updatedPositionSet)
  }, [applyPositionSetUpdate])

  useEffect(() => {
    if (!isColumnActive || filterSupportsContribution(currentSegmentFilters, isColumnActive)) {
      return
    }
    // The write below persists, so it must not strip against a not-yet-loaded portfolio
    if (!pagePortfolio) {
      return
    }

    const cleanedFilters = canKeepSegment ? currentSegmentFilters : removeUnsupportedBaseFilters(currentSegmentFilters)
    if (cleanedFilters !== currentSegmentFilters) {
      applyPositionSetUpdate({
        ...queryStringPositionSets[currentIndex],
        segment: computeNonNullSegment(cleanedFilters, currentNormalization),
      })
    }
  }, [isColumnActive, currentSegmentFilters, currentNormalization, queryStringPositionSets, currentIndex, applyPositionSetUpdate, canKeepSegment, pagePortfolio])

  const handleShowSegmentFilters = useCallback(() => {
    setFilterAnchorEl(anchorEl)
    setAnchorEl(false)
  }, [anchorEl])

  const handleUpdateSegmentFilterRows = useCallback((filterRows) => {
    pendingFilterRowsRef.current = filterRows
  }, [])

  const handleApplySegmentNormalization = useCallback((newNormalization) => {
    const filterRows = pendingFilterRowsRef.current ?? EMPTY_ARRAY
    pendingFilterRowsRef.current = null
    // filterRows is the picker's live, still-open state; currentSegmentFilters is the last-saved
    // value and lags it. computeSegmentFilterUpdate strips tag/positionAttributes once active unless
    // the experiment-of-own-portfolio keep rule applies - this only decides the Contribution/Standalone fallback itself.
    const effectiveNormalization = computeFallbackNormalization(newNormalization, filterRowsSupportContribution(filterRows, isColumnActive))
    updateCurrentPositionSet({
      ...queryStringPositionSets[currentIndex],
      segment: computeSegmentFilterUpdate(filterRows, effectiveNormalization, isColumnActive, isExperimentOfOwnPortfolio && effectiveNormalization === STANDALONE),
    })
    setFilterAnchorEl(null)
  }, [queryStringPositionSets, currentIndex, isColumnActive, isExperimentOfOwnPortfolio, updateCurrentPositionSet])

  const menuOptions = useMemo(() => {
    const handleRemove = () => {
      setAnchorEl(false)
      const filteredPositionSet = filter(queryStringPositionSets, (_, i) => i !== currentIndex)
      updateQueryString({ positionSets: filteredPositionSet })
      savePositionSetsPreference(filteredPositionSet)
    }

    const handleSplitLongShort = () => {
      setAnchorEl(false)
      const newPositionSets = [
        ...queryStringPositionSets,
        {
          ...positionSet.queryStringPositionSet,
          segment: {
            filters: { longShort: "LONG" },
            normalization: CONTRIBUTION,
          },
        }, {
          ...positionSet.queryStringPositionSet,
          segment: {
            filters: { longShort: "SHORT" },
            normalization: CONTRIBUTION,
          },
        },
      ]
      updateQueryString({ positionSets: newPositionSets })
      savePositionSetsPreference(newPositionSets)
    }

    const handleEvaluateAs = () => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        asShort: positionSet.evaluatedAsShort ? undefined : true,
      })
    }

    const handleDisableBase = () => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        base: false,
      })
    }

    const handleUseDefaultBase = () => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        base: undefined,
      })
    }

    const handleSetCustomBase = (base) => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        base,
      })
    }

    const handleNormalizeAs = () => {
      const currentSegment = queryStringPositionSets[currentIndex].segment
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        segment: {
          ...currentSegment,
          normalization: currentSegment.normalization === CONTRIBUTION ? STANDALONE : CONTRIBUTION,
        },
      })
    }

    const handleShowBaseSelector = () => {
      setAnchorEl(false)

      dispatch(DialogCreators.open({
        title: "Search For Base",
        content: <CompareSearch account={accountAlias} modelId={modelId} disableSearchResult={disableSearchResult} itemsActions={generateBaseSelectionActions(handleSetCustomBase)} />,
      }))
    }

    const handleToggleFxHedge = (enabled = false) => {
      updateCurrentPositionSet({
        ...queryStringPositionSets[currentIndex],
        fxHedgeEnabled: enabled,
      })
    }

    let actions = [{
      condition: !positionSet.fixed,
      action: {
        label: "Remove",
        fn: handleRemove,
      },
    }]

    if (!positionSet.unavailable) {
      actions = actions.concat([{
        condition: !positionSet.fixed
          && ![PORTFOLIO, BENCHMARK].includes(positionSet.type)
          && positionSet.longShort === LONG,
        action: {
          label: `Evaluate As ${positionSet.evaluatedAsShort ? "Long" : "Short"}`,
          fn: handleEvaluateAs,
        },
      }, {
        condition: positionSet.longShort === LONG_SHORT,
        action: {
          label: "Split into LONG/SHORT",
          fn: handleSplitLongShort,
          disabled: remainingColumns < 2,
          disabledTooltip: "There is not enough space to add the 2 segments",
        },
      }, {
        condition: !positionSet.fixed,
        action: {
          label: "Select Segment",
          fn: handleShowSegmentFilters,
          disabled: isSegmentFilterPickerDisabled,
          disabledTooltip: segmentFilterPickerDisabledTooltip,
        },
      }, {
        condition: positionSet.normalization,
        action: {
          label: `Normalize as ${positionSet.normalization === CONTRIBUTION ? "Standalone" : "Contributor"}`,
          fn: handleNormalizeAs,
          disabled: positionSet.normalization === STANDALONE && hasUnsupportedContributionFilter,
          disabledTooltip: "Filtering on position tags or attributes is not allowed in contribution mode when active.",
        },
      }, {
        isSpacer: true,
      }, {
        condition: !positionSet.fixed,
        action: {
          label: "Select Custom Base",
          fn: handleShowBaseSelector,
        },
      }, {
        condition: !positionSet.fixed && isContextActive && positionSet.queryStringPositionSet.base !== false,
        action: {
          label: "Disable Base",
          fn: handleDisableBase,
        },
      }, {
        condition: !positionSet.fixed && positionSet.queryStringPositionSet.base !== undefined,
        action: {
          label: "Remove Custom Base",
          fn: handleUseDefaultBase,
        },
      }, {
        condition: !positionSet.fixed && positionSet.fxHedgeEnabled,
        action: {
          label: "Disable FX Hedge",
          fn: () => handleToggleFxHedge(false),
        },
      }, {
        condition: !positionSet.fixed && !positionSet.fxHedgeEnabled,
        action: {
          label: "Enable FX Hedge",
          fn: () => handleToggleFxHedge(true),
          disabled: !positionSet.isFxHedgeSupported,
          disabledTooltip: "FX Hedge is not supported by the current model",
        },
      }])
    }

    return chain(actions)
      .filter(({ condition, isSpacer }) => condition || isSpacer)
      .map(({ action, isSpacer }) => action || isSpacer)
      .value()
  }, [positionSet, currentIndex, queryStringPositionSets, savePositionSetsPreference, updateQueryString, updateCurrentPositionSet, remainingColumns, isContextActive, hasUnsupportedContributionFilter, dispatch, accountAlias, modelId, disableSearchResult, handleShowSegmentFilters, isSegmentFilterPickerDisabled, segmentFilterPickerDisabledTooltip])

  const menu = chain(menuOptions)
    .map((menuOption, index) => {
      if (menuOption === true) {
        if (index === 0 || index === menuOptions.length - 1) { // don't display spacers at start and end
          return null
        }
        return <hr key={index} />
      }
      return <ItemHeaderMenuItem key={index} {...menuOption} />
    })
    .compact()
    .value()

  const title = positionSet.unavailable ? NOT_FOUND_LABEL : positionSet.name

  return <div data-test-class="comparison-item" className="comparison-item-wrapper">
    <OPTooltip title={title}>
      <span className="comparison-item-label">{title}</span>
    </OPTooltip>
    {menu.length > 0 && <>
      <IconButton size="small" onClick={event => { setAnchorEl(event.target) }} data-test-class="comparison-item-menu-button"><MoreVert /></IconButton>
      <Menu
        data-test-class="comparison-item-menu-popover"
        disablePortal
        transformOrigin={{ horizontal: "right" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(false)}
      >
        {menu}
      </Menu>
    </>}
    {filterAnchorEl && <AnalyzeHeaderFilters
      account={accountAlias}
      modelId={modelId}
      assetId={positionSet.queryStringPositionSet?.id}
      assetType={positionSet.queryStringPositionSet?.type}
      startDate={endDate}
      endDate={endDate}
      heapAddFilterId="compare"
      filterOns={filterOns}
      securityIds={securityIds}
      normalization={currentNormalization}
      setNormalization={handleApplySegmentNormalization}
      hasUnsupportedContributionFilter={hasUnsupportedContributionFilter}
      addedFilters={addedFilters}
      updateFilters={handleUpdateSegmentFilterRows}
      anchorElement={filterAnchorEl}
      filtersVisible={!!filterAnchorEl}
      toggleFilters={() => setFilterAnchorEl(null)}
    />}
  </div>
}

CompareItemHeader.propTypes = {
  modelId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  positionSet: POSITION_SET.isRequired,
  remainingColumns: PropTypes.number.isRequired,
  isContextActive: PropTypes.bool.isRequired,
  endDate: DATE.isRequired,
  pagePortfolio: PropTypes.shape({
    id: PropTypes.string,
    alias: PropTypes.string,
  }),
}
